import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Tag as TagIcon,
  Upload,
  Save,
  Trash2,
  ChevronDown,
  ChevronUp,
  Palette,
  AlertCircle,
  CheckCircle,
  Loader2,
  Eye,
  FileText,
  X,
  Plus,
} from 'lucide-react';
import api from '../../api';
import toast from 'react-hot-toast';

/**
 * Per-client-tag branding admin. Pairs with the project-scoped
 * ClientBrandingAdmin — the two coexist so legacy project brandings
 * remain editable while new client tag branding is the primary path.
 *
 * Auto-expands the tag passed as ?tag=<id> in the URL so the BD modal's
 * "Add now →" deep-link lands directly in the editing form.
 */

interface ClientTag {
  tag_id: number;
  tag_name: string;
  tag_type: 'client' | 'internal' | 'rnd';
  active: boolean;
  display_order: number;
}

interface TagBranding {
  id: number;
  client_tag_id: number;
  company_name: string;
  primary_color: string;
  secondary_color: string | null;
  accent_color: string | null;
  logo_filename: string | null;
  logo_s3_key: string | null;
  logo_url?: string | null;
  updated_at: string;
}

interface BrandingForm {
  companyName: string;
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  pendingLogoFile: File | null;
  uploadedS3Key: string | null;
  uploadedFilename: string | null;
  existingFilename: string | null;
  existingS3Key: string | null;
  previewUrl: string | null;
}

const DEFAULT_FORM: BrandingForm = {
  companyName: '',
  primaryColor: '#3B82F6',
  secondaryColor: '#5B82F6',
  accentColor: '#F6823B',
  pendingLogoFile: null,
  uploadedS3Key: null,
  uploadedFilename: null,
  existingFilename: null,
  existingS3Key: null,
  previewUrl: null,
};

const HEX_RE = /^#[0-9A-Fa-f]{6}$/;
const isValidHex = (v: string) => HEX_RE.test(v);

const ClientTagBrandingAdmin: React.FC = () => {
  const [tags, setTags] = useState<ClientTag[]>([]);
  const [brandingByTag, setBrandingByTag] = useState<Record<number, TagBranding | null>>({});
  const [loading, setLoading] = useState(true);
  const [expandedTag, setExpandedTag] = useState<number | null>(null);
  const [forms, setForms] = useState<Record<number, BrandingForm>>({});
  const [saving, setSaving] = useState<number | null>(null);
  const [uploading, setUploading] = useState<number | null>(null);
  const [deleting, setDeleting] = useState<number | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newClientName, setNewClientName] = useState('');
  const [newClientType, setNewClientType] = useState<'client' | 'internal' | 'rnd'>('client');
  const [creating, setCreating] = useState(false);
  const fileInputRefs = useRef<Record<number, HTMLInputElement | null>>({});
  const cardRefs = useRef<Record<number, HTMLDivElement | null>>({});

  const loadAll = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/api/client-tags');
      const fresh: ClientTag[] = Array.isArray(res?.data?.tags) ? res.data.tags : [];
      // Show client tags first; internal/rnd typically don't need branding
      // but stay visible at the bottom for completeness.
      fresh.sort((a, b) => {
        const typeOrder = { client: 1, internal: 2, rnd: 3 } as const;
        if (typeOrder[a.tag_type] !== typeOrder[b.tag_type]) {
          return typeOrder[a.tag_type] - typeOrder[b.tag_type];
        }
        return a.tag_name.localeCompare(b.tag_name);
      });
      setTags(fresh);

      // Fetch branding for every tag in parallel (small dataset, ~10 calls).
      const map: Record<number, TagBranding | null> = {};
      await Promise.all(
        fresh.map(async (t) => {
          try {
            const r = await api.get(`/api/client-tags/${t.tag_id}/branding`);
            map[t.tag_id] = r?.data?.success && r?.data?.data ? r.data.data : null;
          } catch (_) {
            map[t.tag_id] = null;
          }
        })
      );
      setBrandingByTag(map);
    } catch (err: any) {
      toast.error('Failed to load client tags');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  // Deep-link: ?tag=<id> auto-expands that tag's editor (used by the BD
  // New Engagement modal's "Add now →" link).
  useEffect(() => {
    if (loading || tags.length === 0) return;
    const params = new URLSearchParams(window.location.search);
    const tagParam = params.get('tag');
    if (tagParam) {
      const id = Number(tagParam);
      if (Number.isFinite(id) && tags.some((t) => t.tag_id === id)) {
        setExpandedTag(id);
      }
    }
  }, [loading, tags]);

  const openAddModal = () => {
    setNewClientName('');
    setNewClientType('client');
    setShowAddModal(true);
  };

  const handleCreateClient = async () => {
    const name = newClientName.trim();
    if (!name) {
      toast.error('Client name is required');
      return;
    }
    try {
      setCreating(true);
      const res = await api.post('/api/client-tags', { tag_name: name, tag_type: newClientType });
      const created = res?.data?.tag;
      if (!res?.data?.success || !created) {
        throw new Error(res?.data?.error || 'Failed to create client');
      }
      setShowAddModal(false);
      await loadAll();
      const newTag: ClientTag = {
        tag_id: created.tag_id,
        tag_name: created.tag_name,
        tag_type: created.tag_type || newClientType,
        active: created.active !== false,
        display_order: created.display_order ?? 0,
      };
      ensureForm(newTag);
      setExpandedTag(created.tag_id);
      toast.success(`Added "${created.tag_name}"`);
      setTimeout(() => {
        cardRefs.current[created.tag_id]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
    } catch (err: any) {
      toast.error(err?.response?.data?.error || err?.message || 'Failed to create client');
    } finally {
      setCreating(false);
    }
  };

  // Lazily initialise the form for a tag the first time it's expanded.
  const ensureForm = (tag: ClientTag) => {
    setForms((prev) => {
      if (prev[tag.tag_id]) return prev;
      const branding = brandingByTag[tag.tag_id];
      return {
        ...prev,
        [tag.tag_id]: branding
          ? {
              ...DEFAULT_FORM,
              companyName: branding.company_name || tag.tag_name,
              primaryColor: branding.primary_color || DEFAULT_FORM.primaryColor,
              secondaryColor: branding.secondary_color || DEFAULT_FORM.secondaryColor,
              accentColor: branding.accent_color || DEFAULT_FORM.accentColor,
              existingFilename: branding.logo_filename,
              existingS3Key: branding.logo_s3_key,
              previewUrl: branding.logo_url || null,
            }
          : { ...DEFAULT_FORM, companyName: tag.tag_name },
      };
    });
  };

  const toggleExpand = (tag: ClientTag) => {
    if (expandedTag === tag.tag_id) {
      setExpandedTag(null);
      return;
    }
    ensureForm(tag);
    setExpandedTag(tag.tag_id);
  };

  const handleFormChange = (tagId: number, patch: Partial<BrandingForm>) => {
    setForms((prev) => ({ ...prev, [tagId]: { ...(prev[tagId] || DEFAULT_FORM), ...patch } }));
  };

  const handleFileSelect = (tagId: number, file: File) => {
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Logo must be 5MB or smaller');
      return;
    }
    const url = URL.createObjectURL(file);
    handleFormChange(tagId, { pendingLogoFile: file, previewUrl: url, uploadedFilename: file.name });
  };

  const uploadLogo = async (tagId: number) => {
    const form = forms[tagId];
    if (!form || !form.pendingLogoFile) return null;
    try {
      setUploading(tagId);
      const fd = new FormData();
      fd.append('logo', form.pendingLogoFile);
      const res = await api.post(`/api/client-tags/${tagId}/branding/logo`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (!res?.data?.success || !res?.data?.s3Key) {
        throw new Error('Upload did not return an S3 key');
      }
      handleFormChange(tagId, {
        uploadedS3Key: res.data.s3Key,
        uploadedFilename: res.data.filename || form.pendingLogoFile.name,
        pendingLogoFile: null,
      });
      return res.data.s3Key as string;
    } catch (err: any) {
      toast.error(err?.response?.data?.error || err?.message || 'Logo upload failed');
      return null;
    } finally {
      setUploading(null);
    }
  };

  const handleSave = async (tag: ClientTag) => {
    const form = forms[tag.tag_id];
    if (!form) return;
    if (!form.companyName.trim()) {
      toast.error('Company name is required');
      return;
    }
    if (!isValidHex(form.primaryColor)) {
      toast.error('Primary color must be a valid hex (e.g. #1D58A4)');
      return;
    }

    try {
      setSaving(tag.tag_id);

      // Upload pending logo first so we have the S3 key to save with the row.
      let logoS3Key = form.uploadedS3Key || form.existingS3Key || null;
      let logoFilename = form.uploadedFilename || form.existingFilename || null;
      if (form.pendingLogoFile) {
        const key = await uploadLogo(tag.tag_id);
        if (!key) return; // upload toasted its own error
        logoS3Key = key;
        logoFilename = form.pendingLogoFile.name;
      }

      const payload: any = {
        companyName: form.companyName.trim(),
        primaryColor: form.primaryColor,
        secondaryColor: isValidHex(form.secondaryColor) ? form.secondaryColor : null,
        accentColor: isValidHex(form.accentColor) ? form.accentColor : null,
      };
      if (logoS3Key) payload.logoS3Key = logoS3Key;
      if (logoFilename) payload.logoFilename = logoFilename;

      const res = await api.post(`/api/client-tags/${tag.tag_id}/branding`, payload);
      if (res?.data?.success) {
        toast.success(`Saved branding for ${tag.tag_name}`);
        await loadAll(); // refresh so banner status / preview URLs are up to date
      } else {
        toast.error(res?.data?.error || 'Save failed');
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.error || err?.message || 'Failed to save branding');
    } finally {
      setSaving(null);
    }
  };

  const handleDelete = async (tag: ClientTag) => {
    if (!window.confirm(`Remove branding for "${tag.tag_name}"? Exports for this client will fall back to Stemmler defaults.`)) {
      return;
    }
    try {
      setDeleting(tag.tag_id);
      const res = await api.delete(`/api/client-tags/${tag.tag_id}/branding`);
      if (res?.data?.success) {
        toast.success(`Removed branding for ${tag.tag_name}`);
        // Reset local form + reload state
        setForms((prev) => {
          const copy = { ...prev };
          delete copy[tag.tag_id];
          return copy;
        });
        await loadAll();
      } else {
        toast.error(res?.data?.message || 'Delete failed');
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.error || err?.message || 'Failed to delete branding');
    } finally {
      setDeleting(null);
    }
  };

  if (loading) {
    return (
      <div className="p-8 text-center text-gray-500">
        <Loader2 className="w-6 h-6 animate-spin inline mr-2" />
        Loading client tags…
      </div>
    );
  }

  const configuredCount = Object.values(brandingByTag).filter(Boolean).length;
  const missingCount = tags.length - configuredCount;

  return (
    <div className="space-y-4">
      {/* Summary header */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2">
              <TagIcon className="w-4 h-4 text-blue-600" />
              Client Branding
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">
              One branding per client tag. Applied to every engagement, project, and export under that tag.
            </p>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-xs text-gray-600 flex gap-3">
              <span className="flex items-center gap-1">
                <CheckCircle className="w-3.5 h-3.5 text-green-600" />
                {configuredCount} configured
              </span>
              {missingCount > 0 && (
                <span className="flex items-center gap-1">
                  <AlertCircle className="w-3.5 h-3.5 text-amber-600" />
                  {missingCount} using defaults
                </span>
              )}
            </div>
            <button
              type="button"
              onClick={openAddModal}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus className="w-3.5 h-3.5" />
              Add Client
            </button>
          </div>
        </div>
      </div>

      {tags.length === 0 && !loading && (
        <div className="bg-white rounded-lg border border-gray-200 p-8 text-center text-sm text-gray-500">
          <p>No client tags configured yet.</p>
          <button
            type="button"
            onClick={openAddModal}
            className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-3.5 h-3.5" />
            Add your first client
          </button>
        </div>
      )}

      {tags.map((tag) => {
        const branding = brandingByTag[tag.tag_id];
        const form = forms[tag.tag_id];
        const isExpanded = expandedTag === tag.tag_id;
        const isSaving = saving === tag.tag_id;
        const isUploading = uploading === tag.tag_id;
        const isDeleting = deleting === tag.tag_id;
        const dotColor =
          tag.tag_type === 'client' ? 'bg-blue-500' :
          tag.tag_type === 'internal' ? 'bg-purple-500' : 'bg-amber-500';

        return (
          <div key={tag.tag_id} ref={(el) => { cardRefs.current[tag.tag_id] = el; }} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <button
              type="button"
              onClick={() => toggleExpand(tag)}
              className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors text-left"
            >
              <div className="flex items-center gap-3">
                <span className={`inline-block w-2 h-2 rounded-full ${dotColor}`} />
                <div>
                  <div className="font-medium text-gray-900 text-sm flex items-center gap-2">
                    {tag.tag_name}
                    <span className="text-xs text-gray-500 font-normal">
                      {tag.tag_type === 'client' ? 'Client' : tag.tag_type === 'internal' ? 'Internal' : 'R&D / Learning'}
                    </span>
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5">
                    {branding ? (
                      <span className="flex items-center gap-1.5">
                        <span
                          className="inline-block w-3 h-3 rounded-sm border border-gray-300"
                          style={{ backgroundColor: branding.primary_color }}
                          title={branding.primary_color}
                        />
                        {branding.company_name}
                        {branding.logo_s3_key && (
                          <span className="text-green-700"> · logo set</span>
                        )}
                      </span>
                    ) : (
                      <span className="text-amber-700">No branding · using Stemmler defaults</span>
                    )}
                  </div>
                </div>
              </div>
              {isExpanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
            </button>

            {isExpanded && form && (
              <div className="border-t border-gray-100 p-4 space-y-4 bg-gray-50">
                {/* Company / display name */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Company display name
                  </label>
                  <input
                    type="text"
                    value={form.companyName}
                    onChange={(e) => handleFormChange(tag.tag_id, { companyName: e.target.value })}
                    placeholder="Used as the brand name in export headers"
                    className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                {/* Colors */}
                <div className="grid grid-cols-3 gap-3">
                  {(['primaryColor', 'secondaryColor', 'accentColor'] as const).map((key) => {
                    const labels = {
                      primaryColor: 'Primary',
                      secondaryColor: 'Secondary',
                      accentColor: 'Accent',
                    };
                    const val = form[key];
                    const valid = isValidHex(val);
                    return (
                      <div key={key}>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          <Palette className="inline w-3 h-3 mr-0.5" />
                          {labels[key]}
                        </label>
                        <div className="flex items-center gap-1.5">
                          <input
                            type="color"
                            value={valid ? val : '#000000'}
                            onChange={(e) => handleFormChange(tag.tag_id, { [key]: e.target.value })}
                            className="w-9 h-9 rounded border border-gray-300 cursor-pointer flex-shrink-0"
                          />
                          <input
                            type="text"
                            value={val}
                            onChange={(e) => handleFormChange(tag.tag_id, { [key]: e.target.value })}
                            placeholder="#RRGGBB"
                            maxLength={7}
                            className={`flex-1 min-w-0 px-2 py-1.5 text-xs font-mono border rounded-md focus:outline-none focus:ring-1 ${
                              valid ? 'border-gray-300 focus:ring-blue-500' : 'border-red-300 focus:ring-red-500'
                            }`}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Logo */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Logo</label>
                  <div className="flex items-start gap-3">
                    {form.previewUrl ? (
                      <div className="relative w-28 h-16 border border-gray-300 rounded-md bg-white flex items-center justify-center overflow-hidden flex-shrink-0">
                        <img
                          src={form.previewUrl}
                          alt="Logo preview"
                          className="max-w-full max-h-full object-contain"
                          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                        />
                      </div>
                    ) : (
                      <div className="w-28 h-16 border border-dashed border-gray-300 rounded-md bg-white flex items-center justify-center text-xs text-gray-400 flex-shrink-0">
                        No logo
                      </div>
                    )}
                    <div className="flex-1 space-y-1">
                      <input
                        ref={(el) => { fileInputRefs.current[tag.tag_id] = el; }}
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          if (f) handleFileSelect(tag.tag_id, f);
                          e.currentTarget.value = '';
                        }}
                        className="hidden"
                      />
                      <button
                        type="button"
                        onClick={() => fileInputRefs.current[tag.tag_id]?.click()}
                        disabled={isUploading || isSaving}
                        className="px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 disabled:opacity-50 rounded-md flex items-center gap-1.5"
                      >
                        <Upload className="w-3.5 h-3.5" />
                        {form.existingFilename || form.uploadedFilename ? 'Replace logo' : 'Upload logo'}
                      </button>
                      {(form.pendingLogoFile || form.uploadedFilename || form.existingFilename) && (
                        <div className="text-xs text-gray-500 truncate flex items-center gap-1">
                          <FileText className="w-3 h-3" />
                          {form.pendingLogoFile?.name || form.uploadedFilename || form.existingFilename}
                          {form.pendingLogoFile && (
                            <span className="text-amber-700 ml-1">(unsaved)</span>
                          )}
                        </div>
                      )}
                      <p className="text-xs text-gray-500">PNG, SVG, or JPG · 5 MB max</p>
                    </div>
                  </div>
                </div>

                {/* Action buttons */}
                <div className="flex items-center justify-between pt-2 border-t border-gray-200">
                  {branding ? (
                    <button
                      type="button"
                      onClick={() => handleDelete(tag)}
                      disabled={isDeleting || isSaving}
                      className="px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-50 disabled:opacity-50 rounded-md flex items-center gap-1.5"
                    >
                      {isDeleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                      Remove branding
                    </button>
                  ) : <span />}
                  <button
                    type="button"
                    onClick={() => handleSave(tag)}
                    disabled={isSaving || isUploading || !form.companyName.trim()}
                    className="px-4 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed rounded-md flex items-center gap-1.5"
                  >
                    {isSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                    {branding ? 'Save changes' : 'Save branding'}
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      })}

      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => !creating && setShowAddModal(false)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                <Plus className="w-4 h-4 text-blue-600" />
                Add Client
              </h3>
              <button type="button" onClick={() => !creating && setShowAddModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="px-5 py-4 space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Client name</label>
                <input
                  type="text"
                  value={newClientName}
                  onChange={(e) => setNewClientName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !creating) handleCreateClient(); }}
                  autoFocus
                  maxLength={128}
                  placeholder="e.g. Acme Financial"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Type</label>
                <select
                  value={newClientType}
                  onChange={(e) => setNewClientType(e.target.value as 'client' | 'internal' | 'rnd')}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                >
                  <option value="client">Client</option>
                  <option value="internal">Internal</option>
                  <option value="rnd">R&D / Learning</option>
                </select>
              </div>
              <p className="text-xs text-gray-500">
                The new client starts with Stemmler defaults. You can configure its branding from the list afterwards.
              </p>
            </div>
            <div className="flex justify-end gap-2 px-5 py-4 border-t border-gray-100">
              <button
                type="button"
                onClick={() => setShowAddModal(false)}
                disabled={creating}
                className="px-4 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCreateClient}
                disabled={creating || !newClientName.trim()}
                className="px-4 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed rounded-md flex items-center gap-1.5"
              >
                {creating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
                Add Client
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientTagBrandingAdmin;
